import { getConfig } from './config';

function generateRandomString(length: number): string {
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function signIn(): Promise<void> {
  const config = await getConfig();
  const codeVerifier = generateRandomString(64);
  sessionStorage.setItem('pkce_verifier', codeVerifier);
  // Preserve any deep-link path (e.g. /AVP-12345) across the OAuth redirect,
  // since redirect_uri must be the registered root URL.
  sessionStorage.setItem('post_login_path', window.location.pathname + window.location.search);
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const url =
    `https://${config.cognitoDomain}/oauth2/authorize` +
    `?client_id=${config.clientId}` +
    `&response_type=code` +
    `&scope=openid+email` +
    `&redirect_uri=${encodeURIComponent(config.redirectUri)}` +
    `&identity_provider=AmazonFederate` +
    `&code_challenge_method=S256` +
    `&code_challenge=${codeChallenge}`;
  window.location.href = url;
}

async function exchangeCodeForTokens(code: string): Promise<{ id_token?: string; refresh_token?: string } | null> {
  const config = await getConfig();
  const codeVerifier = sessionStorage.getItem('pkce_verifier');
  if (!codeVerifier) return null;

  const resp = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: codeVerifier,
    }),
  });
  sessionStorage.removeItem('pkce_verifier');
  if (!resp.ok) return null;
  return resp.json();
}

// Silently obtain a fresh ID token using the stored refresh token.
// Returns the new ID token, or null if the refresh token is missing,
// expired, or revoked (e.g. by the Federate kill switch).
export async function refreshIdToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('pist_refresh_token');
  if (!refreshToken) return null;
  const config = await getConfig();

  const resp = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  });
  if (!resp.ok) {
    localStorage.removeItem('pist_refresh_token');
    return null;
  }
  const tokens = await resp.json();
  if (!tokens.id_token) return null;
  localStorage.setItem('pist_id_token', tokens.id_token);
  return tokens.id_token;
}

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export interface AuthState {
  idToken: string;
  email: string;
}

// Amazon login alias of the signed-in user, extracted from the ID token.
// cognito:username is like "AmazonFederate_blafois" -- strip the prefix.
export function aliasFromIdToken(idToken: string): string {
  const claims = decodeJwt(idToken);
  if (!claims) return '';
  const username = (claims['cognito:username'] as string) || '';
  if (username.includes('_')) {
    return username.substring(username.indexOf('_') + 1);
  }
  const email = (claims.email as string) || '';
  return username || email.split('@')[0] || '';
}

// Whether the signed-in user holds admin permissions, per the Federate-injected
// `is_admin` claim carried in the ID token. Mirrors the backend check in
// permissions.py (can_view_stats). This only decides whether to surface the
// stats dashboard in the UI — the backend independently enforces the same rule
// on GET /stats (403 otherwise), so this is defence-in-depth, not the control.
export function isAdminFromIdToken(idToken: string): boolean {
  const claims = decodeJwt(idToken);
  if (!claims) return false;
  // Cognito may surface the Federate attribute bare or `custom:`-prefixed.
  const value = ((claims['is_admin'] as string) || (claims['custom:is_admin'] as string) || '')
    .trim()
    .toLowerCase();
  return value === 'has_admin_permissions';
}

function authStateFromToken(idToken: string): AuthState | null {
  const claims = decodeJwt(idToken);
  if (!claims || (claims.exp as number) * 1000 <= Date.now()) return null;
  return {
    idToken,
    email: (claims.email as string) || (claims['cognito:username'] as string) || '',
  };
}

export async function initAuth(): Promise<AuthState | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (code) {
    const tokens = await exchangeCodeForTokens(code);
    if (tokens?.id_token) {
      localStorage.setItem('pist_id_token', tokens.id_token);
      if (tokens.refresh_token) {
        localStorage.setItem('pist_refresh_token', tokens.refresh_token);
      }
      // Strip the ?code=... query and restore the pre-login deep-link path.
      const postLoginPath = sessionStorage.getItem('post_login_path') || window.location.pathname;
      sessionStorage.removeItem('post_login_path');
      window.history.replaceState(null, '', postLoginPath);
    }
  }

  const idToken = localStorage.getItem('pist_id_token');
  if (idToken) {
    const state = authStateFromToken(idToken);
    if (state) return state;
    localStorage.removeItem('pist_id_token');
  }

  // ID token missing or expired -- try a silent renewal via refresh token.
  const renewed = await refreshIdToken();
  if (renewed) {
    const state = authStateFromToken(renewed);
    if (state) return state;
  }
  return null;
}

// Redirect to Federate sign-in automatically, at most once per browser
// session, so a misconfiguration cannot cause a redirect loop. Returns
// true if a redirect was initiated.
export async function tryAutoSignIn(): Promise<boolean> {
  if (sessionStorage.getItem('auto_login_attempted')) return false;
  sessionStorage.setItem('auto_login_attempted', '1');
  await signIn();
  return true;
}
